import CloseIcon from '@mui/icons-material/Close'
import { styled } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'

import { OptionItem } from '../../../../utils'
import { ButtonPrimary, ButtonSecondary } from '../../../Button'
import { Dialog, DialogActions, DialogContent, DialogTitle } from '../../../Dialog'
import { FormField, FormFieldLabel, TextField } from '../../../FormField'
import { FlexLayout } from '../../../Layout'
import { Select } from '../../../Select'
import { Spacer } from '../../../Spacer'
import { Typography } from '../../../Typography'
import { CatalogLabels, getInitialPath } from '../definitions'
import { useCatalog } from '../hooks/useCatalog'

const DialogTitleLayout = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`
const CloseButton = styled('div')`
  cursor: pointer;
`

const DialogBody = styled(DialogContent)`
  padding: 20px 20px 0;
  max-width: 500px;
`

type Props = {
  catalog: ReturnType<typeof useCatalog>
  labels: CatalogLabels
  options: {
    label: string
    value: string
  }[]
}

export const EditDialog = (props: Props) => {
  const { catalog, labels, options } = props

  const [selectedPath, setSelectedPath] = useState('')
  const [description, setDesctiption] = useState('')
  const [positionPath, setPositionPath] = useState<OptionItem>({ label: 'None', value: '' })
  const [availableOptions, setAvailableOptions] = useState<
    {
      label: string
      value: string
    }[]
  >([])

  const handleParentCategoryChange = useCallback(
    (v: string | string[]) => setPositionPath(availableOptions.find((f) => f.value === v[0]) || availableOptions[0]),
    [availableOptions],
  )

  const handleSelectedPathChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSelectedPath(e.target.value),
    [],
  )

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setDesctiption(e.target.value),
    [],
  )

  const close = useCallback(() => {
    setSelectedPath('')
    setDesctiption('')
    setPositionPath({ label: 'None', value: '' })

    catalog.closeEditDialog()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const confirm = useCallback(() => {
    if (catalog.editDialogState.itemId) {
      const success = catalog.editItem(
        catalog.editDialogState.itemId,
        positionPath.value === '' ? selectedPath : positionPath.value + '/' + selectedPath,
        description,
      )
      if (success) {
        catalog.openAlert('Changes saved successfully')
      }
    } else {
      const success = catalog.insertNewItem(selectedPath, description, positionPath.value || '')
      if (success) {
        catalog.openAlert('Item created successfully')
      }
    }

    close()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [description, positionPath, selectedPath])

  useEffect(() => {
    const opt = catalog.nodes.find((f) => f.id === catalog.editDialogState.itemId)

    const filtered = options.filter(
      (f) =>
        !f.value.startsWith(opt?.path || '') ||
        // eslint-disable-next-line @typescript-eslint/no-shadow
        !f.value.split('/').find((f) => opt?.path.split('/').find((e) => e === f)),
    )

    setAvailableOptions(filtered)

    setSelectedPath(opt?.path.split('/').pop() || '')
    setDesctiption(opt?.description || '')
    setPositionPath(options.find((f) => f.value === getInitialPath(opt?.path)) || { label: 'None', value: '' })
  }, [catalog.editDialogState.itemId, catalog.nodes, options])

  return (
    <Dialog data-test-id="edit-dialog-container" open={catalog.editDialogState.open} onClose={close}>
      <DialogTitle>
        <DialogTitleLayout>
          <Typography itemType="h3">
            {catalog.editDialogState.itemId ? `Edit ${labels.topic}` : `Add ${labels.topic}`}
          </Typography>
          <CloseButton data-test-id="catalog-close-edit-dialog" onClick={close}>
            <CloseIcon />
          </CloseButton>
        </DialogTitleLayout>
      </DialogTitle>
      <DialogBody>
        <FlexLayout>
          <Spacer size="sm" />
          <TextField
            data-test-id="catalog-edit-dialog-path-input"
            value={selectedPath}
            error={!selectedPath.length}
            onChange={handleSelectedPathChange}
            label={`${labels.mainColumn}*`}
          />
          <Spacer size="sm" />
          <TextField
            data-test-id="catalog-edit-dialog-description-input"
            value={description}
            error={!description.length}
            onChange={handleDescriptionChange}
            label={`${labels.secondaryColumn}*`}
          />
          <Spacer size="sm" />
          <FormField>
            <FormFieldLabel>Parent Category*</FormFieldLabel>
            <Select
              data-test-id="catalog-edit-dialog-parent-category-select"
              label="Parent Category*"
              onChange={handleParentCategoryChange}
              options={availableOptions}
              value={positionPath}
            />
          </FormField>
        </FlexLayout>
      </DialogBody>
      <DialogActions>
        <ButtonSecondary data-test-id="catalog-edit-dialog-cancel-button" onClick={close}>
          Cancel
        </ButtonSecondary>
        <ButtonPrimary
          data-test-id="catalog-edit-dialog-confirm-button"
          disabled={!selectedPath.length || !description.length || !positionPath}
          onClick={confirm}
        >
          Confirm
        </ButtonPrimary>
      </DialogActions>
    </Dialog>
  )
}
